import {Inject, Injectable, InjectionToken} from "@angular/core";
import {map, Observable, share} from "rxjs";
import {JwtHttpClientService} from "../../http/jwt-http-client.service";
import {ConfigurationService} from "../../configuration/configuration.service";
import {ResourceModule} from "../resource.module";
import {HttpParams, HttpResponse} from "@angular/common/http";

/**
 * Generic service for searching an resource from the server.
 *
 * The #SEARCH_RESOURCE must be registered as a provider in the Angular dependency injection to specify the resource
 * name. This will form part of the URL path
 *
 * Errors are thrown as a #DataClientError
 */

export class SearchSortField {
    field: string;
    dir: string;

    constructor(field: string, dir: string) {
        this.field = field;
        this.dir = dir;
    }
}

@Injectable()
export class SearchService {

    /**
     * Static URL prefix for all server side calls
     * @type {string}
     */
    public static readonly URL_PREFIX = '/dataserver/search/';

    /**
     * Provider lookup key alias that is used during dependency injection
     * @type {InjectionToken}
     */
    public static readonly SEARCH_RESOURCE: InjectionToken<string> = new InjectionToken<string>('searchResource');

    /**
     * Provider lookup key alias that is used during dependency injection
     * @type {InjectionToken}
     */
    public static readonly RESOURCE_VERSION: InjectionToken<string> = new InjectionToken<string>('resourceVersion');

    /**
     * URL root to access the resource ending in '/'. Primary keys and child entities may need to be appended
     */
    protected readonly url: string;

    /**
     * Constructs the service with a resource, Http service, and Error service.
     * The resource may be injected via the #SEARCH_RESOURCE alias
     * @param http HTTP Service
     * @param configService
     * @param nameSpace NameSpace that forms part of the URL
     * @param searchNames Search names for the APP
     * @param resource Resource name that forms part of the URL
     * @param version Resource version that forms part of the URL
     */
    constructor(protected http: JwtHttpClientService,
                protected configService: ConfigurationService,
                @Inject(ResourceModule.RESOURCE_NAMESPACE_KEY) protected nameSpace: string,
                @Inject(ResourceModule.SEARCH_NAMES_KEY) protected searchNames: string[],
                @Inject(SearchService.SEARCH_RESOURCE) protected resource: string,
                @Inject(SearchService.RESOURCE_VERSION) protected version?: number) {
        if (searchNames && searchNames.indexOf(resource) < 0) {
            throw new Error("Search " + resource + " is not supported.");
        }

        let url: string = SearchService.URL_PREFIX + nameSpace + "/" + resource;
        if (version) {
            url = url + ".v" + version;
        }
        url = url + "/";

        this.url = url;
    }

    public getNameSpace(): string {
        return this.nameSpace;
    }

    public getResource(): string {
        return this.resource;
    }

    /**
     * Retrieves the search results
     * @param filter Object containing the filter. All properties of the object will be used to build the search request.
     * @param start record to start result
     * @param limit limit of number of records to retrieve
     * @param sort required sort fields
     * @returns {Observable<R>}
     */
    public search(filter: any, start: number, limit: number, sort: SearchSortField[]): Observable<any> {
        let params: HttpParams = new HttpParams();
        if (limit) {
            params = params.set('limit', limit + "");
        }
        if (start) {
            params= params.set('start', start + "");
        }
        if (filter) {
            let filterRequest: string = this.buildFilterRequest(filter);
            if (filterRequest) {
                params = params.set("filter", filterRequest);
            }
        }
        if (sort) {
            let sortRequest: string = this.buildSortRequest(sort);
            if (sortRequest) {
                params = params.set("sort", sortRequest);
            }
        }
        return this.http.get(this.url, params).pipe(
            map((response: HttpResponse<Object>) => response.body || {}),
            share()
        );
    }

    /**
     * Build the filter request in a http get format. When the value is not a number, it will wrap in quotes. Also it will nest property names when it finds an object.
     * i.e. primaryCategory:{name:'Wine'} will become a parameter with the name primaryCategory.name. Multiple values are passed using comma separated.
     * @param filter Object containing the filters as property values
     * @param initialPath Nested path
     * @returns an string containing the filter in a request format
     */
    private buildFilterRequest(filter: any, initialPath: string = ''): string {
        let params: string[] = [];
        for (var field in filter) {
            if (filter[field] != undefined) {
                let fieldParam;
                if (Array.isArray(filter[field])) {
                    let values = "";
                    filter[field].forEach((a, index) => {
                        if (typeof a === "string") {
                            a = "'" + a.replace("'", "''") + "'";
                        }
                        if (index > 0) {
                            values = values + "," + a;
                        } else {
                            values = a + "";
                        }
                    });
                    if (values) {
                        fieldParam = initialPath + field + ' eq ' + '"' + encodeURI(values) + '"';
                    }
                } else if (typeof filter[field] === "object") {
                    if(filter[field] instanceof Date) {
                        fieldParam = initialPath + field + ' eq "' + filter[field].toISOString() + '"';
                    } else {
                        fieldParam = this.buildFilterRequest(filter[field], initialPath + (initialPath ? '.' : "") + field + ".");
                    }
                } else {
                    let quoted: boolean = typeof filter[field] !== "number";
                    let filterValue = encodeURI(filter[field] + "");
                    fieldParam = initialPath + field + ' eq ' + (quoted ? '"' : '') + filterValue + (quoted ? '"' : '');
                }
                if (fieldParam) {
                    params.push(fieldParam);
                }
            }
        }
        if (params.length > 0) {
            return params.join(",");
        } else {
            return null;
        }
    }

    private buildSortRequest(sortFields: SearchSortField[]): string {
        let params: string[] = sortFields.map(function (sort) {
            return (sort.dir == "desc" ? "-" : "") + sort.field;
        });
        if (params.length > 0) {
            return params.join(",");
        } else {
            return null;
        }
    }

    public canSearch(): Promise<boolean> {
        return SearchService.canSearch(this.configService, this.nameSpace + "/" + this.resource);
    }

    public static canSearch(configService: ConfigurationService, resource: string): Promise<boolean> {
        return configService.getString("resourcePrivilege/search/" + resource).then((privMask: string) => privMask ? configService.getBoolean("authorisation/" + privMask.replace("*", "view")) : false);
    }
}
